import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  X,
  Play,
  BookOpen,
  Clock,
  Eye,
  Star,
  ChevronLeft,
  ChevronRight,
  CheckCircle,
  FileText,
  Video,
  Book,
  Newspaper,
  File,
  FileCheck,
  AlertCircle,
  Loader2
} from 'lucide-react';
import { updateVideoProgress } from '../../api/api';
import { Document, Page, pdfjs } from 'react-pdf';
import mammoth from 'mammoth';

// Set up PDF.js worker
pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.min.js`;

const ResourceDetail = ({ resource, onClose, onNext, onPrevious, hasNext, hasPrevious, onProgressUpdate }) => {
  // ALL HOOKS MUST BE CALLED BEFORE ANY CONDITIONAL RETURNS
  const [isBookmarked, setIsBookmarked] = useState(false);
  const [currentSection, setCurrentSection] = useState(0);
  const [showTranscript, setShowTranscript] = useState(false);
  const [videoProgress, setVideoProgress] = useState({
    progress_percentage: resource?.progress || 0,
    watch_time_seconds: resource?.watch_time_seconds || 0,
    resume_position: resource?.resume_position || 0
  });
  const [isUpdatingProgress, setIsUpdatingProgress] = useState(false);
  const [lastUpdateTime, setLastUpdateTime] = useState(0);
  const [showDocumentViewer, setShowDocumentViewer] = useState(false);
  const [pdfError, setPdfError] = useState(null);
  const [numPages, setNumPages] = useState(0);
  const [pageNumber, setPageNumber] = useState(1);
  const [wordContent, setWordContent] = useState(null);
  const [wordError, setWordError] = useState(null);
  const [wordLoading, setWordLoading] = useState(false);
  const [showError, setShowError] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  
  const videoRef = useRef(null);
  const pdfContainerRef = useRef(null);
  const playerRef = useRef(null);

  // Extract values safely with defaults (before early return)
  const id = resource?.id || null;
  const title = resource?.title || '';
  const description = resource?.description || '';
  const type = resource?.type || resource?.type_label || 'Video';
  const duration = resource?.duration || resource?.duration_formatted || '';
  const views = resource?.views || 0;
  const rating = resource?.rating || '';
  const author = resource?.author || null;
  const course = resource?.course || null;
  const module = resource?.module || null;
  const thumbnail = resource?.thumbnail || null;
  const file_url = resource?.file_url || null;
  const progress = resource?.progress || 0;
  const status = resource?.status || 'Not Started';
  const created_at = resource?.created_at || null;
  const tags = resource?.tags || [];
  const sections = resource?.sections || [];
  const transcript = resource?.transcript || '';
  const objectives = resource?.objectives || [];
  const prerequisites = resource?.prerequisites || [];
  const resumePosition = resource?.resume_position || 0;
  const resourceType = resource?.resource_type || resource?.type || 'video';
  const file_size_formatted = resource?.file_size_formatted || '';

  // ============================================
  // SECURITY: Block all unauthorized actions
  // ============================================
  
  // 1. Block right-click / context menu
  useEffect(() => {
    const handleContextMenu = (e) => {
      // Block right-click on video, document viewer, and entire modal
      if (
        e.target.closest('video') || 
        e.target.closest('.document-viewer') || 
        e.target.closest('.pdf-container') ||
        e.target.closest('.word-container') ||
        e.target.closest('.video-container')
      ) {
        e.preventDefault();
        return false;
      }
    };

    document.addEventListener('contextmenu', handleContextMenu);
    return () => document.removeEventListener('contextmenu', handleContextMenu);
  }, []);

  // 2. Block keyboard shortcuts (F12, Ctrl+S, Ctrl+P, Ctrl+U, Ctrl+C, Ctrl+Shift+I, Ctrl+Shift+J, PrintScreen)
  useEffect(() => {
    const blockKeyboard = (e) => {
      // Block F12, Ctrl+S, Ctrl+P, Ctrl+U, Ctrl+C, Ctrl+Shift+I, Ctrl+Shift+J, PrintScreen
      if (
        e.key === "F12" ||
        (e.ctrlKey && ["s", "p", "u", "c", "S", "P", "U", "C"].includes(e.key)) ||
        (e.ctrlKey && e.shiftKey && ["i", "I", "j", "J"].includes(e.key)) ||
        (e.ctrlKey && e.key === "PrintScreen") ||
        e.key === "PrintScreen"
      ) {
        e.preventDefault();
        e.stopPropagation();
        return false;
      }
    };

    const handleKeyUp = (e) => {
      if (e.key === 'PrintScreen') {
        e.preventDefault();
        return false;
      }
    };

    window.addEventListener("keydown", blockKeyboard);
    window.addEventListener("keyup", handleKeyUp);
    return () => {
      window.removeEventListener("keydown", blockKeyboard);
      window.removeEventListener("keyup", handleKeyUp);
    };
  }, []);

  // 3. Block copy, cut, paste on video and document content
  useEffect(() => {
    const handleCopy = (e) => {
      if (
        e.target.closest('video') || 
        e.target.closest('.pdf-container') || 
        e.target.closest('.word-container') ||
        e.target.closest('.document-viewer') ||
        e.target.closest('.video-container')
      ) {
        e.preventDefault();
        return false;
      }
    };

    const handleCut = (e) => {
      if (
        e.target.closest('video') || 
        e.target.closest('.pdf-container') || 
        e.target.closest('.word-container') ||
        e.target.closest('.document-viewer') ||
        e.target.closest('.video-container')
      ) {
        e.preventDefault();
        return false;
      }
    };

    const handlePaste = (e) => {
      if (
        e.target.closest('video') || 
        e.target.closest('.pdf-container') || 
        e.target.closest('.word-container') ||
        e.target.closest('.document-viewer') ||
        e.target.closest('.video-container')
      ) {
        e.preventDefault();
        return false;
      }
    };

    const handleDragStart = (e) => {
      if (
        e.target.closest('video') || 
        e.target.closest('.pdf-container') || 
        e.target.closest('.word-container') ||
        e.target.closest('.document-viewer')
      ) {
        e.preventDefault();
        return false;
      }
    };

    document.addEventListener('copy', handleCopy);
    document.addEventListener('cut', handleCut);
    document.addEventListener('paste', handlePaste);
    document.addEventListener('dragstart', handleDragStart);

    return () => {
      document.removeEventListener('copy', handleCopy);
      document.removeEventListener('cut', handleCut);
      document.removeEventListener('paste', handlePaste);
      document.removeEventListener('dragstart', handleDragStart);
    };
  }, []);

  // 4. Disable video download and picture-in-picture
  useEffect(() => {
    if (videoRef.current) {
      videoRef.current.controlsList = 'nodownload noremoteplayback noplaybackrate';
      videoRef.current.disablePictureInPicture = true;
    }
  }, [videoRef.current]);

  // Determine file type from URL
  const getFileType = (url) => {
    if (!url) return null;
    const extension = url.split('.').pop()?.toLowerCase();
    if (['pdf'].includes(extension)) return 'pdf';
    if (['doc', 'docx'].includes(extension)) return 'word';
    if (['mp4', 'webm', 'ogg', 'm3u8'].includes(extension)) return 'video';
    if (['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(extension)) return 'image';
    return 'unknown';
  };

  const fileType = getFileType(file_url);
  const isDocument = ['pdf', 'word'].includes(fileType);
  const isPdf = fileType === 'pdf';
  const isWord = fileType === 'word';
  const isVideo = resourceType === 'video' || resourceType === 'Video' || fileType === 'video';

  // Use the resource ID as the video ID (only for videos)
  const actualVideoId = isVideo ? id : null;

  // Initialize video progress from resource data (only for videos)
  useEffect(() => {
    if (resource && isVideo) {
      setVideoProgress({
        progress_percentage: resource.progress || 0,
        watch_time_seconds: resource.watch_time_seconds || 0,
        resume_position: resource.resume_position || 0
      });
    }
  }, [resource, isVideo]);

  // Update video progress to API (only for videos)
  const updateVideoProgressToAPI = useCallback(async (videoId, progressData) => {
    if (isUpdatingProgress || !videoId || !isVideo) return;
    
    try {
      setIsUpdatingProgress(true);
      const response = await updateVideoProgress(videoId, progressData);
      
      if (onProgressUpdate) {
        onProgressUpdate(videoId, progressData);
      }
      
    } catch (error) {
      console.error('Failed to update video progress:', error);
    } finally {
      setIsUpdatingProgress(false);
    }
  }, [isUpdatingProgress, onProgressUpdate, isVideo]);

  const handleVideoProgress = useCallback(async (videoElement) => {
    if (!videoElement || !actualVideoId || !isVideo) return;

    const currentTime = videoElement.currentTime;
    const duration = videoElement.duration;
    
    if (!duration || duration === 0) return;

    const percentage = Math.round((currentTime / duration) * 100);
    const watchTime = Math.floor(currentTime);

    const shouldUpdate = Math.abs(percentage - videoProgress.progress_percentage) >= 5 || 
                         Math.abs(watchTime - videoProgress.watch_time_seconds) >= 10;

    if (shouldUpdate) {
      const progressData = {
        progress_percentage: Math.min(percentage, 100),
        watch_time_seconds: watchTime,
        resume_position: Math.floor(currentTime)
      };

      setVideoProgress(progressData);
      await updateVideoProgressToAPI(actualVideoId, progressData);
    }
  }, [actualVideoId, videoProgress, updateVideoProgressToAPI, isVideo]);

  const handleTimeUpdate = useCallback((e) => {
    if (!isVideo) return;
    const video = e.target;
    if (!video) return;

    const now = Date.now();
    if (now - lastUpdateTime < 3000) return;
    setLastUpdateTime(now);

    handleVideoProgress(video);
  }, [handleVideoProgress, lastUpdateTime, isVideo]);

  const handleVideoEnded = useCallback(async () => {
    if (!isVideo) return;
    const video = videoRef.current;
    if (!video || !actualVideoId) return;

    const progressData = {
      progress_percentage: 100,
      watch_time_seconds: Math.floor(video.duration),
      resume_position: Math.floor(video.duration)
    };

    setVideoProgress(progressData);
    await updateVideoProgressToAPI(actualVideoId, progressData);
  }, [actualVideoId, updateVideoProgressToAPI, isVideo]);

  // Handle video loaded metadata - set resume position (only for videos)
  const handleLoadedMetadata = useCallback(() => {
    if (!isVideo) return;
    const video = videoRef.current;
    if (!video || !resumePosition) return;

    if (resumePosition > 0 && resumePosition < video.duration) {
      video.currentTime = resumePosition;
    }
  }, [resumePosition, isVideo]);

  // Handle video pause - update progress (only for videos)
  const handlePause = useCallback(() => {
    if (!isVideo) return;
    const video = videoRef.current;
    if (!video || !actualVideoId) return;

    const currentTime = video.currentTime;
    const duration = video.duration;
    
    if (!duration || duration === 0) return;

    const percentage = Math.round((currentTime / duration) * 100);
    const progressData = {
      progress_percentage: Math.min(percentage, 100),
      watch_time_seconds: Math.floor(currentTime),
      resume_position: Math.floor(currentTime)
    };

    setVideoProgress(progressData);
    updateVideoProgressToAPI(actualVideoId, progressData);
  }, [actualVideoId, updateVideoProgressToAPI, isVideo]);

  // Handle video error
  const handleVideoError = useCallback(() => {
    setErrorMessage('Failed to load video. Please try again.');
    setShowError(true);
  }, []);

  // Load Word document content
  const loadWordDocument = useCallback(async () => {
    if (!file_url) return;
    
    setWordLoading(true);
    setWordError(null);
    setWordContent(null);
    
    try {
      const response = await fetch(file_url);
      if (!response.ok) {
        throw new Error('Failed to load document');
      }
      
      const arrayBuffer = await response.arrayBuffer();
      const result = await mammoth.convertToHtml({ arrayBuffer });
      setWordContent(result.value);
      setWordLoading(false);
      setShowDocumentViewer(true);
    } catch (error) {
      console.error('Error loading Word document:', error);
      setWordError('Failed to load document. Please try again.');
      setWordLoading(false);
    }
  }, [file_url]);

  // Handle PDF viewing
  const handleOpenPDF = () => {
    if (file_url) {
      setShowDocumentViewer(true);
      setPageNumber(1);
      setPdfError(null);
    }
  };

  // Handle Word document viewing
  const handleOpenWord = () => {
    if (file_url) {
      loadWordDocument();
    }
  };

  // Handle generic document viewing
  const handleOpenDocument = () => {
    if (file_url) {
      if (isPdf) {
        handleOpenPDF();
      } else if (isWord) {
        handleOpenWord();
      } else {
        // For other documents, try to open directly
        window.open(file_url, '_blank');
      }
    }
  };

  // Close document viewer
  const closeDocumentViewer = () => {
    setShowDocumentViewer(false);
    setPdfError(null);
    setNumPages(0);
    setPageNumber(1);
    setWordContent(null);
    setWordError(null);
    setWordLoading(false);
  };

  // Handle PDF load success
  const onDocumentLoadSuccess = ({ numPages }) => {
    setNumPages(numPages);
    setPdfError(null);
  };

  // Handle PDF load error
  const onDocumentLoadError = (error) => {
    console.error('PDF load error:', error);
    setPdfError('Failed to load PDF. Please try again.');
  };

  // Handle page navigation
  const goToPrevPage = () => {
    setPageNumber((prev) => Math.max(prev - 1, 1));
  };

  const goToNextPage = () => {
    setPageNumber((prev) => Math.min(prev + 1, numPages));
  };

  // NOW we can do the early return after all hooks are declared
  if (!resource) return null;

  // Helper functions
  const getTypeIcon = (type) => {
    if (isDocument) {
      if (isPdf) return <FileText size={20} className="text-red-600" />;
      if (isWord) return <FileText size={20} className="text-blue-600" />;
      return <FileText size={20} className="text-blue-600" />;
    }
    const icons = {
      'Video': <Video size={20} className="text-green-600" />,
      'Guide': <Book size={20} className="text-blue-600" />,
      'Article': <FileText size={20} className="text-purple-600" />,
      'Case Study': <Newspaper size={20} className="text-orange-600" />
    };
    return icons[type] || <FileText size={20} className="text-gray-600" />;
  };

  const getTypeColor = (type) => {
    if (isDocument) {
      if (isPdf) return 'bg-red-50 text-red-700 border-red-200';
      if (isWord) return 'bg-blue-50 text-blue-700 border-blue-200';
      return 'bg-blue-50 text-blue-700 border-blue-200';
    }
    const colors = {
      'Video': 'bg-green-50 text-green-700 border-green-200',
      'Guide': 'bg-blue-50 text-blue-700 border-blue-200',
      'Article': 'bg-purple-50 text-purple-700 border-purple-200',
      'Case Study': 'bg-orange-50 text-orange-700 border-orange-200'
    };
    return colors[type] || 'bg-gray-50 text-gray-700 border-gray-200';
  };

  const formatTime = (seconds) => {
    if (!seconds) return '00:00';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const displayProgress = isVideo ? (videoProgress.progress_percentage || progress || 0) : 0;

  const getFileTypeDisplay = () => {
    if (isPdf) return 'PDF Document';
    if (isWord) return 'Word Document';
    if (isVideo) return 'Video';
    return 'Document';
  };

  // Render Word document viewer with security
  const renderWordViewer = () => {
    return (
      <div 
        className="w-full bg-gray-100 rounded-xl p-4 document-viewer word-container"
        onContextMenu={(e) => {
          e.preventDefault();
          return false;
        }}
      >
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <button
              onClick={closeDocumentViewer}
              className="bg-red-500 text-white px-3 py-1.5 rounded-lg text-sm hover:bg-red-600 transition"
            >
              Close
            </button>
            <span className="text-sm text-gray-600 ml-2">
               Word Document
            </span>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xs text-gray-400">
              {file_size_formatted || ''}
            </span>
          </div>
        </div>

        <div 
          className="bg-white rounded-lg p-6 overflow-auto max-h-[500px]"
          style={{ 
            userSelect: 'none',
            WebkitUserSelect: 'none',
            MozUserSelect: 'none',
            msUserSelect: 'none',
            WebkitUserDrag: 'none'
          }}
          onCopy={(e) => {
            e.preventDefault();
            return false;
          }}
          onCut={(e) => {
            e.preventDefault();
            return false;
          }}
          onPaste={(e) => {
            e.preventDefault();
            return false;
          }}
          onDragStart={(e) => {
            e.preventDefault();
            return false;
          }}
        >
          {wordLoading ? (
            <div className="flex items-center justify-center h-64">
              <div className="text-center">
                <Loader2 size={32} className="animate-spin text-blue-500 mx-auto" />
                <p className="mt-4 text-gray-600">Loading document...</p>
              </div>
            </div>
          ) : wordError ? (
            <div className="flex flex-col items-center justify-center h-64 p-8">
              <AlertCircle size={64} className="text-red-400 mb-4" />
              <p className="text-red-500 font-medium">{wordError}</p>
              <button
                onClick={() => {
                  setWordError(null);
                  loadWordDocument();
                }}
                className="mt-4 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition"
              >
                Retry
              </button>
            </div>
          ) : wordContent ? (
            <div 
              className="word-content prose max-w-none"
              dangerouslySetInnerHTML={{ __html: wordContent }}
              style={{
                fontFamily: 'Arial, sans-serif',
                lineHeight: '1.6',
                color: '#333'
              }}
            />
          ) : (
            <div className="flex flex-col items-center justify-center h-64">
              <FileText size={64} className="text-gray-400 mb-4" />
              <p className="text-gray-600">No content available</p>
            </div>
          )}
        </div>

        {/* Security notice */}
        <div className="mt-2 text-xs text-center text-gray-400">
          🔒 Secure viewing - No download, copy, or screenshot available
        </div>
      </div>
    );
  };

  return (
    <>
      <div 
        className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4"
        onContextMenu={(e) => {
          e.preventDefault();
          return false;
        }}
      >
        <div className="bg-white rounded-3xl w-full max-w-5xl max-h-[90vh] overflow-hidden shadow-2xl animate-in fade-in slide-in-from-bottom-4 duration-300">
          
          {/* Error Toast */}
          {showError && (
            <div className="fixed top-4 right-4 z-50 bg-red-600 text-white px-6 py-4 rounded-lg shadow-xl flex items-center gap-3 max-w-md">
              <AlertCircle size={20} />
              <span>{errorMessage}</span>
              <button onClick={() => setShowError(false)} className="ml-4 hover:opacity-70">
                <X size={18} />
              </button>
            </div>
          )}

          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-gray-100">
            <div className="flex items-center gap-3 flex-wrap">
              <span className={`px-3 py-1 rounded-full text-xs font-medium border ${getTypeColor(type)}`}>
                {/* {getTypeIcon(type)} */}
                <span className="ml-1">{getFileTypeDisplay()}</span>
              </span>
              {isVideo && displayProgress === 100 && (
                <span className="flex items-center gap-1 text-xs text-green-600 bg-green-50 px-3 py-1 rounded-full">
                  <CheckCircle size={14} />
                  Completed
                </span>
              )}
              {isVideo && status && status !== 'Completed' && displayProgress < 100 && (
                <span className={`text-xs px-3 py-1 rounded-full ${
                  displayProgress > 0 ? 'bg-orange-50 text-orange-600' : 'bg-gray-50 text-gray-600'
                }`}>
                  {displayProgress > 0 ? 'In Progress' : 'Not Started'}
                </span>
              )}
              {isUpdatingProgress && (
                <span className="text-xs text-gray-400 animate-pulse">Saving progress...</span>
              )}
              {(isPdf || isWord) && showDocumentViewer && (
                <span className="text-xs text-blue-600 bg-blue-50 px-3 py-1 rounded-full">
                  🔒 Secure View
                </span>
              )}
            </div>
            
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 rounded-xl transition-colors"
            >
              <X size={20} />
            </button>
          </div>

          {/* Content */}
          <div className="overflow-y-auto max-h-[calc(90vh-80px)] p-6">
            {showDocumentViewer && isPdf ? (
              // Embedded PDF Viewer with react-pdf - FULL SECURITY
              <div 
                ref={pdfContainerRef}
                className="w-full bg-gray-100 rounded-xl p-4 relative pdf-container document-viewer"
                style={{ 
                  userSelect: 'none',
                  WebkitUserSelect: 'none',
                  MozUserSelect: 'none',
                  msUserSelect: 'none',
                  WebkitUserDrag: 'none'
                }}
                onContextMenu={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  return false;
                }}
                onCopy={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  return false;
                }}
                onCut={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  return false;
                }}
                onPaste={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  return false;
                }}
                onDragStart={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  return false;
                }}
              >
                {/* PDF Controls */}
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-2">
                    <button
                      onClick={closeDocumentViewer}
                      className="bg-red-500 text-white px-3 py-1.5 rounded-lg text-sm hover:bg-red-600 transition"
                    >
                      Close
                    </button>
                  </div>
                  <div className="flex items-center gap-3">
                    <button
                      onClick={goToPrevPage}
                      disabled={pageNumber <= 1}
                      className={`px-3 py-1.5 rounded-lg text-sm transition ${
                        pageNumber <= 1
                          ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                          : 'bg-blue-500 text-white hover:bg-blue-600'
                      }`}
                    >
                      Previous
                    </button>
                    <span className="text-sm text-gray-600">
                      Page {pageNumber} of {numPages || '...'}
                    </span>
                    <button
                      onClick={goToNextPage}
                      disabled={pageNumber >= numPages}
                      className={`px-3 py-1.5 rounded-lg text-sm transition ${
                        pageNumber >= numPages
                          ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                          : 'bg-blue-500 text-white hover:bg-blue-600'
                      }`}
                    >
                      Next
                    </button>
                  </div>
                </div>

                {/* PDF Viewer */}
                <div className="flex justify-center bg-white rounded-lg overflow-auto max-h-[500px]">
                  {pdfError ? (
                    <div className="flex flex-col items-center justify-center h-96 p-8">
                      <FileText size={64} className="text-red-400 mb-4" />
                      <p className="text-red-500 font-medium">{pdfError}</p>
                      <button
                        onClick={() => {
                          setPdfError(null);
                          setShowDocumentViewer(false);
                        }}
                        className="mt-4 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition"
                      >
                        Try Again
                      </button>
                    </div>
                  ) : (
                    <Document
                      file={file_url}
                      onLoadSuccess={onDocumentLoadSuccess}
                      onLoadError={onDocumentLoadError}
                      loading={
                        <div className="flex items-center justify-center h-96">
                          <Loader2 size={32} className="animate-spin text-blue-500" />
                        </div>
                      }
                    >
                      <Page
                        pageNumber={pageNumber}
                        width={Math.min(window.innerWidth - 100, 800)}
                        renderAnnotationLayer={false}
                        renderTextLayer={false}
                        loading={
                          <div className="flex items-center justify-center h-96">
                            <Loader2 size={32} className="animate-spin text-blue-500" />
                          </div>
                        }
                      />
                    </Document>
                  )}
                </div>

                {/* Security Overlay - Prevents selection and screenshots */}
                <div 
                  className="absolute inset-0 pointer-events-none"
                  style={{ zIndex: 5 }}
                  onContextMenu={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    return false;
                  }}
                />
                
                {/* Security notice */}
                <div className="mt-2 text-xs text-center text-gray-400">
                  🔒 Secure PDF - No download, copy, or screenshot available
                </div>
              </div>
            ) : showDocumentViewer && isWord ? (
              // Word Document Viewer
              renderWordViewer()
            ) : (
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                
                {/* Main Content - Left */}
                <div className="lg:col-span-2">
                  {/* Thumbnail/Video Player/Document Preview */}
                  <div 
                    className="relative rounded-2xl overflow-hidden bg-gray-900 aspect-video mb-4 video-container"
                    onContextMenu={(e) => {
                      e.preventDefault();
                      return false;
                    }}
                  >
                    {isVideo ? (
                      <video
                        ref={videoRef}
                        src={file_url || 'https://sample-videos.com/video321/mp4/720/big_buck_bunny_720p_1mb.mp4'}
                        controls
                        className="w-full h-full object-cover"
                        poster={thumbnail}
                        onTimeUpdate={handleTimeUpdate}
                        onEnded={handleVideoEnded}
                        onLoadedMetadata={handleLoadedMetadata}
                        onPause={handlePause}
                        onError={handleVideoError}
                        controlsList="nodownload noremoteplayback noplaybackrate"
                        disablePictureInPicture
                        onContextMenu={(e) => {
                          e.preventDefault();
                          return false;
                        }}
                        onCopy={(e) => {
                          e.preventDefault();
                          return false;
                        }}
                      />
                    ) : isDocument ? (
                   <div className="w-full h-full flex flex-col items-center justify-center bg-gradient-to-br from-blue-50 to-blue-100">
  {isPdf ? (
    <FileText size={80} className="text-red-500 mb-4" />
  ) : isWord ? (
    <FileText size={80} className="text-blue-500 mb-4" />
  ) : (
    <FileText size={80} className="text-blue-500 mb-4" />
  )}
  <p className="text-gray-600 text-sm font-medium">
    {isPdf ? 'PDF Document' : isWord ? 'Word Document' : 'Document'}
  </p>
  <p className="text-gray-400 text-xs mt-1">{file_url?.split('/').pop() || 'Document'}</p>
  <div className="flex gap-2 mt-3">
    {isPdf && (
      <span className="px-3 py-1 bg-red-500 text-white text-xs rounded-full">
        PDF
      </span>
    )}
    {isWord && (
      <span className="px-3 py-1 bg-blue-500 text-white text-xs rounded-full">
        Word
      </span>
    )}
    <span className="px-3 py-1 bg-gray-500 text-white text-xs rounded-full">
      {file_size_formatted || 'Unknown size'}
    </span>
  </div>
  {/* Document icon only - no button */}
</div>
                    ) : (
                      <img
                        src={thumbnail || 'https://images.unsplash.com/photo-1490645935967-10de6ba17061?w=800'}
                        alt={title}
                        className="w-full h-full object-cover"
                      />
                    )}
                    
                    {isVideo && displayProgress > 0 && displayProgress < 100 && (
                      <div className="absolute bottom-0 left-0 right-0 h-1 bg-gray-700">
                        <div
                          className="h-full bg-green-500 transition-all duration-500"
                          style={{ width: `${displayProgress}%` }}
                        />
                      </div>
                    )}
                    {isVideo && displayProgress === 100 && (
                      <div className="absolute bottom-0 left-0 right-0 h-1 bg-green-500">
                        <div className="h-full w-full bg-green-500" />
                      </div>
                    )}

                    {/* Watermark for video protection */}
                    {isVideo && (
                      <div className="absolute top-4 right-4 pointer-events-none opacity-20">
                        <div className="bg-black/50 text-white text-xs px-3 py-1 rounded-full">
                          Protected Content
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Title & Meta */}
                  <h1 className="text-2xl font-bold text-gray-900">{title}</h1>
                  
                  {course && (
                    <p className="text-sm text-green-600 font-medium mt-1">
                      {course.title}
                      {module && <span className="text-gray-400"> • {module.title}</span>}
                    </p>
                  )}

                  <div className="flex flex-wrap items-center gap-4 mt-3 text-sm text-gray-500">
                    {duration && !isDocument && (
                      <span className="flex items-center gap-1">
                        <Clock size={14} />
                        {duration}
                      </span>
                    )}
                    {file_size_formatted && isDocument && (
                      <span className="flex items-center gap-1">
                        <File size={14} />
                        {file_size_formatted}
                      </span>
                    )}
                    {views !== undefined && (
                      <span className="flex items-center gap-1">
                        <Eye size={14} />
                        {typeof views === 'number' ? views.toLocaleString() : views} views
                      </span>
                    )}
                    {/* {rating && (
                      <span className="flex items-center gap-1 text-orange-500">
                        <Star size={14} fill="currentColor" />
                        {typeof rating === 'number' ? rating.toFixed(1) : rating}
                      </span>
                    )} */}
                    {author && (
                      <span className="flex items-center gap-1">
                        By {author.name}
                      </span>
                    )}
                    {created_at && (
                      <span className="flex items-center gap-1">
                        Added {new Date(created_at).toLocaleDateString()}
                      </span>
                    )}
                  </div>

                  {/* {description && (
                    <div className="mt-4 p-4 bg-gray-50 rounded-xl">
                      <p className="text-gray-700 text-sm leading-relaxed">{description}</p>
                    </div>
                  )} */}

                  {objectives && objectives.length > 0 && (
                    <div className="mt-4">
                      <h3 className="font-semibold text-gray-900 mb-2">Learning Objectives</h3>
                      <ul className="space-y-1">
                        {objectives.map((obj, index) => (
                          <li key={index} className="flex items-start gap-2 text-sm text-gray-600">
                            <CheckCircle size={16} className="text-green-500 mt-0.5 flex-shrink-0" />
                            {obj}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {sections && sections.length > 0 && (
                    <div className="mt-4">
                      <h3 className="font-semibold text-gray-900 mb-2">Sections</h3>
                      <div className="space-y-2">
                        {sections.map((section, index) => (
                          <div
                            key={index}
                            className={`p-3 rounded-xl border transition cursor-pointer ${
                              currentSection === index
                                ? 'border-green-500 bg-green-50'
                                : 'border-gray-200 hover:border-gray-300'
                            }`}
                            onClick={() => setCurrentSection(index)}
                          >
                            <div className="flex items-center justify-between">
                              <span className="font-medium text-gray-900">
                                {section.title || `Section ${index + 1}`}
                              </span>
                              {section.duration && (
                                <span className="text-xs text-gray-500">{section.duration}</span>
                              )}
                            </div>
                            {section.completed && (
                              <CheckCircle size={14} className="text-green-500 mt-1" />
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {transcript && (
                    <div className="mt-4">
                      <button
                        onClick={() => setShowTranscript(!showTranscript)}
                        className="text-sm font-medium text-green-600 hover:text-green-700"
                      >
                        {showTranscript ? 'Hide Transcript' : 'Show Transcript'}
                      </button>
                      {showTranscript && (
                        <div className="mt-2 p-4 bg-gray-50 rounded-xl max-h-48 overflow-y-auto">
                          <p className="text-sm text-gray-700 whitespace-pre-wrap">{transcript}</p>
                        </div>
                      )}
                    </div>
                  )}

                  {tags && tags.length > 0 && (
                    <div className="flex flex-wrap gap-2 mt-4">
                      {tags.map((tag, index) => (
                        <span
                          key={index}
                          className="px-3 py-1 bg-gray-100 text-gray-600 text-xs rounded-full"
                        >
                          #{tag}
                        </span>
                      ))}
                    </div>
                  )}
                </div>

                {/* Sidebar - Right */}
                <div className="lg:col-span-1">
                  <div className="sticky top-0 space-y-4">
                    {/* Progress Card - Only for Videos */}
                    {isVideo && (
                      <div className="bg-gray-50 rounded-2xl p-4">
                        <h4 className="text-sm font-semibold text-gray-700 mb-3">Your Progress</h4>
                        <div className="flex items-center justify-between text-sm mb-1">
                          <span className="text-gray-600">
                            {displayProgress === 100 ? 'Completed' : displayProgress > 0 ? 'In Progress' : 'Not Started'}
                          </span>
                          <span className="font-semibold text-gray-900">{displayProgress}%</span>
                        </div>
                        <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                          <div
                            className="h-full bg-green-500 rounded-full transition-all duration-500"
                            style={{ width: `${displayProgress}%` }}
                          />
                        </div>
                        
                        {videoProgress.watch_time_seconds > 0 && (
                          <div className="mt-2 text-xs text-gray-500">
                            Watched: {formatTime(videoProgress.watch_time_seconds)}
                            {duration && ` / ${duration}`}
                          </div>
                        )}
                        
                        <button 
                          className="w-full mt-4 bg-green-600 text-white px-4 py-2.5 rounded-xl text-sm font-medium hover:bg-green-700 transition"
                          onClick={() => {
                            if (videoRef.current) {
                              if (videoRef.current.paused) {
                                videoRef.current.play();
                              } else {
                                videoRef.current.pause();
                              }
                            }
                          }}
                        >
                          {displayProgress > 0 ? 'Continue Learning' : 'Start Learning'}
                        </button>
                      </div>
                    )}

                    {/* Document Card - For Documents */}
                    {isDocument && file_url && (
                      <div className={`rounded-2xl p-4 ${isPdf ? 'bg-red-50' : 'bg-blue-50'}`}>
                        <h4 className="text-sm font-semibold text-gray-700 mb-3">Document Details</h4>
                        <div className="flex items-center gap-2 text-sm text-gray-600">
                          <FileCheck size={16} className={isPdf ? 'text-red-500' : 'text-blue-500'} />
                          <span className="truncate">{file_url?.split('/').pop() || 'Document'}</span>
                        </div>
                        {file_size_formatted && (
                          <div className="mt-1 text-xs text-gray-400">
                            Size: {file_size_formatted}
                          </div>
                        )}
                        <div className="mt-1 text-xs text-gray-500">
                          Type: {isPdf ? 'PDF Document' : isWord ? 'Word Document' : 'Document'}
                        </div>
                        {isPdf && (
                          <div className="mt-1 text-xs text-red-600">
                            Secure PDF - No Download Available
                          </div>
                        )}
                        {isWord && (
                          <div className="mt-1 text-xs text-blue-600">
                            Secure Word Document - No Download Available
                          </div>
                        )}
                        
                        <button
                          onClick={handleOpenDocument}
                          className={`w-full mt-3 text-white px-4 py-2.5 rounded-xl text-sm font-medium transition flex items-center justify-center gap-2 ${
                            isPdf ? 'bg-red-600 hover:bg-red-700' : 'bg-blue-600 hover:bg-blue-700'
                          }`}
                        >
                          <FileCheck size={16} />
                          {isPdf ? 'View PDF' : isWord ? 'Open Document' : 'View Document'}
                        </button>
                      </div>
                    )}

                    {/* Prerequisites */}
                    {prerequisites && prerequisites.length > 0 && (
                      <div className="bg-yellow-50 border border-yellow-200 rounded-2xl p-4">
                        <h4 className="text-sm font-semibold text-yellow-800 mb-2">Prerequisites</h4>
                        <ul className="text-sm text-yellow-700 space-y-1">
                          {prerequisites.map((prereq, index) => (
                            <li key={index} className="flex items-center gap-2">
                              <span>•</span>
                              {prereq}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {/* Protected Content Notice */}
                    <div className="border-l-4 border-red-500 pl-3 py-2 bg-red-50 rounded-r-lg">
                      <p className="text-sm font-medium text-red-600">🔒 Protected Content</p>
                      <p className="text-xs text-red-500 mt-0.5">Recording or redistribution prohibited</p>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Navigation Footer - Hide when document viewer is open */}
          {!showDocumentViewer && (hasNext || hasPrevious) && (
            <div className="flex items-center justify-between p-4 border-t border-gray-100 bg-gray-50">
              <button
                onClick={onPrevious}
                disabled={!hasPrevious}
                className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium transition ${
                  hasPrevious
                    ? 'text-gray-700 hover:bg-gray-200'
                    : 'text-gray-300 cursor-not-allowed'
                }`}
              >
                <ChevronLeft size={18} />
                Previous
              </button>
              
              <span className="text-sm text-gray-500">
                {isVideo ? `${displayProgress}% Complete` : (isDocument ? 'Document' : 'Resource')}
              </span>
              
              <button
                onClick={onNext}
                disabled={!hasNext}
                className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium transition ${
                  hasNext
                    ? 'text-gray-700 hover:bg-gray-200'
                    : 'text-gray-300 cursor-not-allowed'
                }`}
              >
                Next
                <ChevronRight size={18} />
              </button>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default ResourceDetail;